/**
 * `textEntry` — type an answer, compared against an accept list after
 * normalization. Exact match only in Phase 1; fuzzy matching is deferred,
 * because Levenshtein over normalized Hebrew is its own correctness project and
 * exact match already proves the seam.
 *
 * Both sides are normalized, not just the learner's input. An accept value
 * authored with niqqud must match an answer typed without it, and vice versa —
 * normalizing one side only would make grading depend on how carefully the author
 * typed.
 *
 * With a `judge` (D93), an answer no accepted string matches is marked by the AI's
 * judgement of it, handed in by the host. None yet is "not judged": `correct: null`,
 * the D32 state, never a zero that reads as wrong.
 */

import { isTextEntryResponse } from '@cet/asset-contract';
import type { ItemResponse, ItemVerdict, Judgement, Locale, TextEntrySchemeDoc } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { normalize } from '../normalize.js';
import { graded, unanswered } from '../verdicts.js';

export function scoreTextEntry(
  itemId: string,
  scheme: TextEntrySchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
  judgement?: Judgement,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isTextEntryResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is a textEntry item, so its response must be { text: string }. ` +
        `Received keys: ${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  const given = normalize(response.text, scheme.text);

  // Whitespace is not an answer (D32). Checked after normalization, so a field
  // containing only a no-break space pasted from a document counts as blank.
  if (given === '') {
    return unanswered(itemId, scheme.points);
  }

  const correct = scheme.accept.some((accepted) => normalize(accepted, scheme.text) === given);
  if (correct || scheme.judge === undefined) {
    return graded(
      itemId,
      { awarded: correct ? scheme.points : 0, max: scheme.points },
      scheme.feedback,
      correct,
      locale,
    );
  }

  // A judgement counts only for this answer and these points: one for an earlier answer, or made
  // when the item was worth something else, is no judgement of this one.
  if (judgement === undefined || judgement.text !== response.text || judgement.max !== scheme.points) {
    return { itemId, answered: true, correct: null, points: 0, max: scheme.points };
  }
  const awarded = Math.min(Math.max(Math.trunc(judgement.points), 0), scheme.points);
  // A rubric's scores by criterion and the AI's notes ride along as facts (D110); the points are the total.
  const verdict: ItemVerdict = {
    itemId, answered: true, correct: judgement.pass, points: awarded, max: scheme.points,
    ...(judgement.scores === undefined ? {} : { scores: judgement.scores }),
    ...(judgement.flags === undefined || judgement.flags.length === 0 ? {} : { flags: judgement.flags }),
  };
  const message = [judgement.strength, judgement.fix].filter((line) => typeof line === 'string' && line.trim() !== '').join('\n');
  return message === '' ? verdict : { ...verdict, message };
}
